import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Building2, GraduationCap, Download, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full px-4 py-3 bg-white border border-[#E2E8F0] text-[#0A0D14] text-sm focus:outline-none focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/15 transition-all placeholder:text-[#94A3B8]";
const labelCls = "block font-mono-x text-[11px] uppercase tracking-[0.15em] text-[#64748B] mb-2";

const Honeypot = ({ value, onChange }) => (
  <input
    type="text"
    name="website"
    value={value}
    onChange={onChange}
    tabIndex={-1}
    autoComplete="off"
    aria-hidden="true"
    className="absolute left-[-9999px] top-0 h-0 w-0 opacity-0"
  />
);

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const scoreOf = (f) => {
  const s = { lt500: 12, "500-1000": 18, "1000-5000": 22, "5000+": 25 }[f.student_strength] || 0;
  const e = { yes: 10, planning: 18, no: 25 }[f.has_ecell] || 0;
  const sp = { yes: 25, can_arrange: 18, no: 8 }[f.has_space] || 0;
  const fu = { yes: 25, maybe: 15, no: 8 }[f.funding_support] || 0;
  return s + e + sp + fu;
};

const verdictOf = (score) =>
  score >= 80
    ? { title: "High-voltage match", copy: "Your institution is primed for a frantiger labs setup. Expect a call within 48 hours." }
    : score >= 55
    ? { title: "Strong signal", copy: "Solid foundations. A short readiness sprint and your campus is venture-ready." }
    : { title: "Early signal — let's talk", copy: "Every great incubator started here. We'll co-design the roadmap with your management." };

const VibeCheckForm = () => {
  const empty = {
    institution_name: "", contact_name: "", role: "", email: "", phone: "", city: "",
    student_strength: "", has_ecell: "", has_space: "", funding_support: "", message: "", website: "",
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const mountedAt = useRef(Date.now());
  const score = useMemo(() => scoreOf(form), [form]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/vibe-check`, { ...form, score, elapsed_ms: Date.now() - mountedAt.current });
      setDone(true);
      toast.success("Vibe Check received. Our campus team will reach out within 48 hours.");
    } catch {
      toast.error("Something glitched. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const v = verdictOf(score);
    return (
      <motion.div
        data-testid="vibe-check-success"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="border border-[#0055FF]/30 bg-[#EBF2FF] p-10 text-center"
      >
        <CheckCircle2 className="w-10 h-10 text-[#0055FF] mx-auto" />
        <div className="mt-5 font-display font-black text-5xl text-[#0055FF]" style={{ fontWeight: 900 }}>{score}/100</div>
        <h3 className="mt-2 font-display font-semibold text-xl text-[#0A0D14]">{v.title}</h3>
        <p className="mt-3 text-sm text-[#64748B] max-w-md mx-auto leading-relaxed">{v.copy} A confirmation is on its way to your inbox.</p>
      </motion.div>
    );
  }

  return (
    <form data-testid="institution-vibe-check-form" onSubmit={submit} className="relative">
      <Honeypot value={form.website} onChange={set("website")} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Institution name" testId="vibe-field-institution">
          <input required data-testid="vibe-input-institution" className={inputCls} value={form.institution_name} onChange={set("institution_name")} placeholder="e.g. St. Xavier's College" />
        </Field>
        <Field label="City" testId="vibe-field-city">
          <input required data-testid="vibe-input-city" className={inputCls} value={form.city} onChange={set("city")} placeholder="Bengaluru" />
        </Field>
        <Field label="Your name" testId="vibe-field-name">
          <input required data-testid="vibe-input-name" className={inputCls} value={form.contact_name} onChange={set("contact_name")} placeholder="Dr. Asha Rao" />
        </Field>
        <Field label="Your role" testId="vibe-field-role">
          <input required data-testid="vibe-input-role" className={inputCls} value={form.role} onChange={set("role")} placeholder="Dean / HOD / Placement Head" />
        </Field>
        <Field label="Official email" testId="vibe-field-email">
          <input required type="email" data-testid="vibe-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@college.edu" />
        </Field>
        <Field label="Phone" testId="vibe-field-phone">
          <input required data-testid="vibe-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 98XXX XXXXX" />
        </Field>
        <Field label="Student strength" testId="vibe-field-strength">
          <select required data-testid="vibe-select-strength" className={inputCls} value={form.student_strength} onChange={set("student_strength")}>
            <option value="">Select…</option>
            <option value="lt500">Under 500</option>
            <option value="500-1000">500 – 1,000</option>
            <option value="1000-5000">1,000 – 5,000</option>
            <option value="5000+">5,000+</option>
          </select>
        </Field>
        <Field label="Existing E-Cell?" testId="vibe-field-ecell">
          <select required data-testid="vibe-select-ecell" className={inputCls} value={form.has_ecell} onChange={set("has_ecell")}>
            <option value="">Select…</option>
            <option value="yes">Yes, active</option>
            <option value="planning">Planning one</option>
            <option value="no">No E-Cell</option>
          </select>
        </Field>
        <Field label="Space for a lab?" testId="vibe-field-space">
          <select required data-testid="vibe-select-space" className={inputCls} value={form.has_space} onChange={set("has_space")}>
            <option value="">Select…</option>
            <option value="yes">Yes, dedicated space</option>
            <option value="can_arrange">Can be arranged</option>
            <option value="no">Not currently</option>
          </select>
        </Field>
        <Field label="Willing to back student funding?" testId="vibe-field-funding">
          <select required data-testid="vibe-select-funding" className={inputCls} value={form.funding_support} onChange={set("funding_support")}>
            <option value="">Select…</option>
            <option value="yes">Yes, actively</option>
            <option value="maybe">Open to it</option>
            <option value="no">Not yet</option>
          </select>
        </Field>
        <div className="sm:col-span-2" data-testid="vibe-field-message">
          <label className={labelCls}>Anything we should know? (optional)</label>
          <textarea data-testid="vibe-input-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Your campus, your ambitions…" />
        </div>
      </div>

      <div className="mt-7 border border-[#0055FF]/20 bg-[#F8FAFC] p-5">
        <div className="flex items-center justify-between mb-2.5">
          <span className="font-mono-x text-[11px] uppercase tracking-[0.15em] text-[#64748B]">Live eligibility score</span>
          <span data-testid="vibe-score-value" className="font-display font-black text-xl text-[#0055FF]" style={{ fontWeight: 900 }}>{score}/100</span>
        </div>
        <div className="h-2 bg-[#E2E8F0] overflow-hidden">
          <motion.div
            data-testid="vibe-score-bar"
            className="h-full bg-[#0055FF]"
            animate={{ width: `${score}%` }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <p className="mt-2.5 font-mono-x text-[10px] uppercase tracking-[0.15em] text-[#64748B]">
          {score > 0 ? verdictOf(score).title : "Answer the four questions to light up your score"}
        </p>
      </div>

      <button
        type="submit"
        data-testid="institution-vibe-check-submit-btn"
        disabled={busy}
        className="mt-7 w-full flex items-center justify-center gap-3 px-7 py-4 bg-[#0055FF] text-white font-mono-x text-sm uppercase tracking-[0.15em] hover:bg-[#0043CC] transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
        {busy ? "Transmitting…" : "Run the Vibe Check"}
      </button>
    </form>
  );
};

const PITCH_LETTER = `FRANTIGER LABS — CAMPUS PITCH NOTE
==================================

To the Management,

frantiger labs sets up a full incubation space inside our campus —
built for institutions without an in-house entrepreneurship cell.

What our college gets:
- A 5-stage venture lifecycle for students: Ideation, Validation,
  Mentorship, Funding and Go-To-Market.
- A decade of frantiger legacy and 100+ years of combined consulting
  experience, on campus, working directly with students.
- Access to frantiger's network of 100+ angel & seed investors and
  its portfolio of brands for pilots and first customers.
- Exclusive SaaS credits, legal/IP support and demo days.

What it costs the institution: zero setup friction. frantiger labs
co-designs the model with the management.

Next step: a 30-minute Vibe Check call with the frantiger team.

— A student who wants to build here
`;

const StudentInviteForm = () => {
  const empty = { student_name: "", email: "", phone: "", college_name: "", city: "", course_year: "", why: "", website: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const mountedAt = useRef(Date.now());
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/campus-invite`, { ...form, elapsed_ms: Date.now() - mountedAt.current });
      setDone(true);
      toast.success("Invite logged. Your campus envoy kit is heading to your inbox.");
    } catch {
      toast.error("Something glitched. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const blob = new Blob([PITCH_LETTER], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "frantiger-labs-campus-pitch.txt";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Pitch note downloaded. Go win that meeting.");
  };

  if (done) {
    return (
      <motion.div
        data-testid="campus-invite-success"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="border border-[#0055FF]/30 bg-[#EBF2FF] p-10 text-center"
      >
        <CheckCircle2 className="w-10 h-10 text-[#0055FF] mx-auto" />
        <h3 className="mt-4 font-display font-semibold text-xl text-[#0A0D14]">You're officially a campus envoy.</h3>
        <p className="mt-3 text-sm text-[#64748B] max-w-md mx-auto leading-relaxed">
          We've logged your invite and emailed your envoy kit. Grab the pitch note below and
          get that meeting with your management — we'll take it from there.
        </p>
        <button
          data-testid="campus-invite-download-btn"
          onClick={download}
          className="mt-6 inline-flex items-center gap-3 px-7 py-4 bg-[#0055FF] text-white font-mono-x text-sm uppercase tracking-[0.15em] hover:bg-[#0043CC] transition-colors"
        >
          <Download className="w-4 h-4" /> Download Pitch Note
        </button>
      </motion.div>
    );
  }

  return (
    <form data-testid="student-invite-campus-form" onSubmit={submit} className="relative">
      <Honeypot value={form.website} onChange={set("website")} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Your name" testId="invite-field-name">
          <input required data-testid="invite-input-name" className={inputCls} value={form.student_name} onChange={set("student_name")} placeholder="Aarav Sharma" />
        </Field>
        <Field label="Your email" testId="invite-field-email">
          <input required type="email" data-testid="invite-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@student.edu" />
        </Field>
        <Field label="Phone" testId="invite-field-phone">
          <input required data-testid="invite-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 98XXX XXXXX" />
        </Field>
        <Field label="Course & year" testId="invite-field-course">
          <input required data-testid="invite-input-course" className={inputCls} value={form.course_year} onChange={set("course_year")} placeholder="B.Tech CSE, 2nd year" />
        </Field>
        <Field label="College name" testId="invite-field-college">
          <input required data-testid="invite-input-college" className={inputCls} value={form.college_name} onChange={set("college_name")} placeholder="Your college" />
        </Field>
        <Field label="City" testId="invite-field-city">
          <input required data-testid="invite-input-city" className={inputCls} value={form.city} onChange={set("city")} placeholder="Pune" />
        </Field>
        <div className="sm:col-span-2" data-testid="invite-field-why">
          <label className={labelCls}>Why does your campus need frantiger labs?</label>
          <textarea required data-testid="invite-input-why" rows={4} className={inputCls} value={form.why} onChange={set("why")} placeholder="Tell us about the builders on your campus, and who in management we should meet…" />
        </div>
      </div>
      <button
        type="submit"
        data-testid="student-invite-campus-submit-btn"
        disabled={busy}
        className="mt-7 w-full flex items-center justify-center gap-3 px-7 py-4 bg-[#0A0D14] text-white font-mono-x text-sm uppercase tracking-[0.15em] hover:bg-[#0055FF] transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />}
        {busy ? "Transmitting…" : "Invite frantiger labs"}
      </button>
    </form>
  );
};

const CtaForms = () => {
  const [tab, setTab] = useState("institution");
  return (
    <section id="apply" className="bg-white py-24 lg:py-32 border-b border-[#E2E8F0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <span className="font-mono-x text-xs uppercase tracking-[0.25em] text-[#0055FF]">/ TWO DOORS IN</span>
          <h2 className="mt-5 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#0A0D14] leading-tight">
            Pick your door. <span className="font-serif-accent text-[#0055FF]">We'll open it.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 grid grid-cols-2 border border-[#E2E8F0]">
            <button
              data-testid="tab-vibe-check-institution"
              onClick={() => setTab("institution")}
              className={`flex items-center justify-center gap-3 px-4 py-4 font-mono-x text-xs sm:text-sm uppercase tracking-[0.15em] transition-colors ${
                tab === "institution" ? "bg-[#0055FF] text-white" : "bg-white text-[#64748B] hover:text-[#0055FF]"
              }`}
            >
              <Building2 className="w-4 h-4" /> For Institutions
            </button>
            <button
              data-testid="tab-invite-campus-student"
              onClick={() => setTab("student")}
              className={`flex items-center justify-center gap-3 px-4 py-4 font-mono-x text-xs sm:text-sm uppercase tracking-[0.15em] transition-colors ${
                tab === "student" ? "bg-[#0055FF] text-white" : "bg-white text-[#64748B] hover:text-[#0055FF]"
              }`}
            >
              <GraduationCap className="w-4 h-4" /> For Students
            </button>
          </div>

          <div id={tab === "student" ? "apply-student" : undefined} className="border border-t-0 border-[#E2E8F0] p-6 sm:p-10 bg-white">
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.35 }}
              >
                {tab === "institution" ? (
                  <>
                    <p className="mb-8 text-sm text-[#64748B] leading-relaxed">
                      The <span className="text-[#0A0D14] font-semibold">Vibe Check</span> measures your
                      institution's intent and readiness to back student entrepreneurship. Answer honestly —
                      your score updates live.
                    </p>
                    <VibeCheckForm />
                  </>
                ) : (
                  <>
                    <p className="mb-8 text-sm text-[#64748B] leading-relaxed">
                      No E-Cell on your campus? <span className="text-[#0A0D14] font-semibold">Be the reason it gets one.</span>{" "}
                      Introduce us to your management and we'll set up frantiger labs inside your campus.
                    </p>
                    <StudentInviteForm />
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CtaForms;
